/**
 * The little red/blue count bubble on a tab or a pill.
 *
 * One component because the same rule was copy-pasted in at least five places —
 * BottomNav twice, ChatScreen twice, AllHistoryScreen — each repeating both the
 * `> 9 ? '9+' : n` formatting and the widen-for-two-characters sizing. Five
 * copies is five chances for one of them to cap at a different number or forget
 * to widen, and nothing would have caught it.
 *
 * Styled inline: src/index.css is committed pre-compiled Tailwind, so a utility
 * that is not already in that bundle renders nothing at all.
 */

import type { CSSProperties } from 'react';

/** Counts above this show as "9+". */
export const UNREAD_BADGE_MAX = 9;

/** The shared formatter, exported so a caller that needs only the text can use it. */
export function formatUnreadCount(count: number): string {
  return count > UNREAD_BADGE_MAX ? `${UNREAD_BADGE_MAX}+` : String(count);
}

export function UnreadBadge({
  count,
  ringColor = '#0D0D0D',
  style,
}: {
  count: number;
  /** The colour the badge sits on, drawn as a ring so it reads as separate. */
  ringColor?: string;
  style?: CSSProperties;
}) {
  // Nothing to show. Returning null rather than an empty circle means callers
  // can drop it in without guarding.
  if (!Number.isFinite(count) || count <= 0) return null;

  const wide = count > UNREAD_BADGE_MAX;

  return (
    <span
      aria-label={`${count} unread`}
      style={{
        position: 'absolute',
        // Two characters need more room than one; a fixed circle clipped "9+".
        minWidth: wide ? 26 : 20,
        height: wide ? 22 : 20,
        paddingLeft: wide ? 4 : 0,
        paddingRight: wide ? 4 : 0,
        borderRadius: 999,
        backgroundColor: '#E74C3C',
        color: '#ffffff',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `2px solid ${ringColor}`,
        pointerEvents: 'none',
        ...style,
      }}
    >
      {formatUnreadCount(count)}
    </span>
  );
}
